import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import { Authenticated, AuthLoading, Unauthenticated } from "@/components/auth-gate";
import { AppShell } from "@/components/shell/app-shell";
import { Landing } from "@/components/landing/landing";
import { Splash } from "@/components/shell/splash";
import { BoardRow } from "@/components/home/board-row";
import { NewBoardDialog } from "@/components/home/new-board-dialog";
import { InviteDialog } from "@/components/home/invite-dialog";
import { AddRoomTile } from "@/components/home/add-room-tile";
import { MemberStack } from "@/components/home/member-stack";
import { SectionHeading } from "@/components/home/section-heading";
import { SinceYouWereHere } from "@/components/home/since-you-were-here";
import { Toaster } from "@/components/ui/sonner";
import { WorkspaceSwitcher } from "@/components/workspaces/workspace-switcher";
import { fullDate } from "@/lib/format";

export const Route = createFileRoute("/")({
  component: HomePage,
});

function HomePage() {
  const { t } = useTranslation("home");
  return (
    <>
      <AuthLoading>
        <Splash subtitle={t("splash.waking")} />
      </AuthLoading>
      <Unauthenticated>
        <Landing />
      </Unauthenticated>
      <Authenticated>
        <SignedIn />
      </Authenticated>
      <Toaster />
    </>
  );
}

function SignedIn() {
  const { t } = useTranslation("home");
  const data = useQuery(api.boards.listForMyWorkspace);
  const workspace = useQuery(api.workspaces.getMyWorkspace);
  const members = useQuery(api.workspaces.getMembers);
  const stats = useQuery(api.boards.statsForMyWorkspace);
  const houses = useQuery(api.houses.listMine);
  const plan = useQuery(api.billing.getMyPlan);

  const totalItems = useMemo(() => {
    if (!stats) return 0;
    return Object.values(stats).reduce((sum, s) => sum + s.itemCount, 0);
  }, [stats]);

  // Stamp this visit once the home is known to exist. A missed stamp only
  // means "since you were here" counts from a little earlier next time.
  const markSeen = useMutation(api.home.markSeen);
  const homeReady = Boolean(data && workspace);
  useEffect(() => {
    if (!homeReady) return;
    markSeen().catch(() => {});
  }, [homeReady, markSeen]);

  if (data === undefined || workspace === undefined) {
    return <Splash subtitle={t("splash.loadingBoards")} />;
  }
  if (data === null || workspace === null) {
    return <RedirectTo to="/onboarding" />;
  }

  const rooms = data.boards.filter((b) => b.kind === "room");
  const freeform = data.boards.filter((b) => b.kind === "freeform");
  const existingRoomTypes = new Set(
    rooms
      .filter((b) => !b.recipeId)
      .map((b) => b.roomType)
      .filter((t): t is string => Boolean(t)),
  );
  // A house's rooms together under its name, the others after them.
  const roomGroups = [
    ...(houses ?? []).map((house) => ({ house, list: rooms.filter((b) => b.houseId === house._id) })),
    { house: null, list: rooms.filter((b) => !houses?.some((h) => h._id === b.houseId)) },
  ].filter(({ list }) => list.length > 0);
  const ordered = roomGroups.flatMap(({ list }) => list);
  // The invite code is the owner's to hand out, as in settings.
  const isOwner = members?.some((m) => m.isMe && m.isOwner) ?? false;
  // A client of the team looks at the rooms it was let into; it makes none.
  const isTeam = workspace.role === "team";
  // In a design studio, only roles that may change rooms make them.
  const canRooms = isTeam && workspace.can.rooms;
  const team = (members ?? []).filter((m) => m.role === "team");
  const existingRecipeIds = new Set(
    rooms.map((b) => b.recipeId).filter((id): id is string => Boolean(id)),
  );

  return (
    <AppShell
      width="wide"
      rightSlot={
        <>
          {isOwner ? <InviteDialog /> : null}
          {canRooms ? <NewBoardDialog /> : null}
        </>
      }
    >
      <div className="grid gap-14 sm:gap-20">
        <Masthead
          workspaceName={workspace.name}
          memberCount={members ? team.length : workspace.memberCount}
          members={team}
          roomCount={rooms.length}
          themeCount={freeform.length}
          totalItems={totalItems}
          // An agency's workspace is a project shown to a client, not a couple's flat.
          intro={!isTeam ? "introClient" : plan?.plan === "studio" ? "introStudio" : "intro"}
        />

        <SinceYouWereHere />

        <section className="grid gap-2">
          <SectionHeading label={t("sections.rooms")} count={rooms.length} />
          <ul className="stagger">
            {roomGroups.flatMap(({ house, list }) => [
              ...(house || roomGroups.length > 1
                ? [
                    <li key={house?._id ?? "elsewhere"} className="flex items-baseline justify-between gap-4 pt-4 first:pt-0">
                      <p className="text-2xs uppercase tracking-[0.18em] text-ink-3">
                        {house ? house.name : t("plans.otherRooms", { ns: "plans" })}
                      </p>
                      {house ? (
                        // The whole house at once: every room on its plan.
                        <Link
                          to="/houses/$houseId"
                          params={{ houseId: house._id }}
                          className="text-xs text-ink-3 underline underline-offset-2 decoration-line-strong hover:text-ink hover:decoration-ink"
                        >
                          {t("house.open", { ns: "plans" })}
                        </Link>
                      ) : null}
                    </li>,
                  ]
                : []),
              ...list.map((board) => (
                <li key={board._id}>
                  <BoardRow
                    index={ordered.indexOf(board) + 1}
                    board={board}
                    itemCount={stats?.[board._id as string]?.itemCount}
                    lastItemAt={stats?.[board._id as string]?.lastItemAt}
                  />
                </li>
              )),
            ])}
            {canRooms ? (
              <li>
                <AddRoomTile
                  existingRoomTypes={existingRoomTypes}
                  existingRecipeIds={existingRecipeIds}
                  variant="row"
                  index={rooms.length + 1}
                />
              </li>
            ) : null}
          </ul>
          {houses && canRooms ? (
            // Still there once a house is made: plans change, and a floor or another house can follow.
            <p className="pt-2 text-xs text-ink-3">
              {t(houses.length === 0 ? "plans.homeHint" : "plans.againHint", { ns: "plans" })}{" "}
              <Link
                to="/plans"
                className="text-ink underline underline-offset-2 decoration-line-strong hover:decoration-ink"
              >
                {t(houses.length === 0 ? "plans.homeLink" : "plans.againLink", { ns: "plans" })}
              </Link>
            </p>
          ) : null}
        </section>

        <section className="grid gap-2">
          <SectionHeading
            label={t("sections.themes")}
            count={freeform.length}
          />
          {freeform.length === 0 ? (
            canRooms ? <EmptyThemes /> : null
          ) : (
            <ul className="stagger">
              {freeform.map((board, idx) => (
                <li key={board._id}>
                  <BoardRow
                    index={idx + 1}
                    board={board}
                    itemCount={stats?.[board._id as string]?.itemCount}
                    lastItemAt={stats?.[board._id as string]?.lastItemAt}
                  />
                </li>
              ))}
              {canRooms ? (
                <li>
                  <NewBoardDialog variant="row" index={freeform.length + 1} />
                </li>
              ) : null}
            </ul>
          )}
        </section>

      </div>
    </AppShell>
  );
}

function Masthead({
  workspaceName,
  memberCount,
  members,
  roomCount,
  themeCount,
  totalItems,
  intro,
}: {
  workspaceName: string;
  memberCount: number;
  members: Parameters<typeof MemberStack>[0]["members"];
  roomCount: number;
  themeCount: number;
  totalItems: number;
  intro: "intro" | "introStudio" | "introClient";
}) {
  // useTranslation re-renders this on a language switch, so formatting on
  // every render keeps the date localized (fullDate caches its formatter).
  const { t } = useTranslation("home");
  const [now] = useState(() => Date.now());
  const today = fullDate(now);
  return (
    <header className="grid gap-6 pt-6 sm:pt-10">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="text-2xs uppercase tracking-[0.18em] text-ink-3">
            {t("masthead.eyebrow")}
          </span>
          <WorkspaceSwitcher />
        </div>
        <MemberStack members={members} />
      </div>
      <h1 className="font-display text-4xl sm:text-display text-balance leading-[0.95] tracking-tight text-ink">
        <span className="italic">{workspaceName}</span>
        <span className="text-accent">.</span>
      </h1>
      <p className="max-w-prose text-pretty text-base sm:text-lg text-ink-2 leading-relaxed">
        {t(`masthead.${intro}`)}
      </p>
      <dl className="mt-2 flex flex-wrap items-baseline gap-x-8 gap-y-2 text-xs text-ink-3">
        <Stat label={t("masthead.stats.today")} value={today} />
        <Stat
          label={t("masthead.stats.rooms")}
          value={String(roomCount).padStart(2, "0")}
          numeric
        />
        <Stat
          label={t("masthead.stats.themes")}
          value={String(themeCount).padStart(2, "0")}
          numeric
        />
        <Stat
          label={t("masthead.stats.items")}
          value={String(totalItems).padStart(2, "0")}
          numeric
        />
        <Stat
          label={t("masthead.stats.members")}
          value={String(memberCount)}
          numeric
        />
      </dl>
    </header>
  );
}

function Stat({
  label,
  value,
  numeric,
}: {
  label: string;
  value: string;
  numeric?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-2xs uppercase tracking-wider text-ink-4">{label}</dt>
      <dd
        className={
          numeric ? "text-sm text-ink num tabular-nums" : "text-sm text-ink"
        }
      >
        {value}
      </dd>
    </div>
  );
}

function EmptyThemes() {
  const { t } = useTranslation("home");
  return (
    <div className="grid gap-3 border-b border-line py-12 text-center">
      <p className="font-display italic text-xl text-ink-2">
        {t("emptyThemes.title")}
      </p>
      <p className="text-xs text-ink-3 max-w-prose mx-auto">
        {t("emptyThemes.body")}
      </p>
      <div className="mt-2 flex justify-center">
        <NewBoardDialog />
      </div>
    </div>
  );
}

function RedirectTo({ to }: { to: "/login" | "/onboarding" }) {
  const { t } = useTranslation("shell");
  const navigate = useNavigate();
  useEffect(() => {
    navigate({ to });
  }, [navigate, to]);
  return <Splash subtitle={t("redirecting")} />;
}
